/**
 * A quest item (10 §3): the radio set of mission 31, the strike beacon of 35. It rides in a named
 * supply pod, so the flyover matters: smash that pod, or never kick it out, and the mission is
 * lost at the drop. Opening the pod puts it on his back (it weighs something); Interact at the
 * `deliverToPropId` hands it over (`deliverItem`) or sets it down and arms the objective
 * (`reachDistance`). When the mission has no such pod at all he carries it from the start.
 */

import type { Ctx } from '../ctx';
import { OBJECTIVES } from '../tuning';
import type { PropDef } from '../types';
import { completeObjective, dist, failObjective, propDef, propDistance } from './common';

const POD_LETTERS = 'ABC';

function carry(ctx: Ctx, weight: number): void {
  ctx.world.objective.item = 'carried';
  ctx.world.ranger.questWeight = weight;
}

/** Called by `openPod`: if this is the pod the item rides in, he has it now - whatever else he could carry. */
export function takeQuestItem(ctx: Ctx, podIndex: number): void {
  const item = ctx.statics.def.objective.questItem;
  if (!item || ctx.world.objective.item !== 'pod' || item.pod !== podIndex) return;
  carry(ctx, item.weight);
  ctx.events.push({ type: 'pickup', what: `${item.name} TAKEN` });
}

/** Every ground tick: where the item is, and whether it has just been lost with its pod. */
export function stepQuestItem(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  const o = w.objective;
  const item = statics.def.objective.questItem;
  if (!item || o.item !== 'pod') return;
  const pod = w.insertion.pods.find((p) => p.index === item.pod);
  if (!pod) {
    // No such pod in this mission (insertion skipped, nothing packed): it came down on his back.
    if (o.groundTicks === 0) carry(ctx, item.weight);
    return;
  }
  const letter = POD_LETTERS[item.pod] ?? '';
  if (pod.state === 'destroyed') {
    o.item = 'lost';
    failObjective(ctx, `THE ${item.name} WAS LOST WITH POD ${letter}`);
  } else if (pod.state === 'aboard') {
    o.item = 'lost';
    failObjective(ctx, `THE ${item.name} NEVER LEFT THE AIRCRAFT - POD ${letter} WAS NOT DROPPED`);
  }
}

/** The prop to hand the item to, if he has it and is close enough. */
export function deliveryInReach(ctx: Pick<Ctx, 'world' | 'statics'>): PropDef | null {
  const { world: w, statics } = ctx;
  const to = statics.def.objective.deliverToPropId;
  if (w.objective.item !== 'carried' || to === undefined) return null;
  const prop = propDef(statics.def, to);
  if (!prop) return null;
  return propDistance(prop, w.ranger.x, w.ranger.z) <= OBJECTIVES.useReach ? prop : null;
}

/** Interact at the hand-over point. For `reachDistance` this is what arms the objective. */
export function deliverQuestItem(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  const o = w.objective;
  const item = statics.def.objective.questItem;
  if (!item || o.item !== 'carried') return;
  o.item = 'delivered';
  w.ranger.questWeight = 0;
  const placed = statics.def.objective.kind === 'reachDistance';
  if (placed && o.armedTick < 0) o.armedTick = o.groundTicks;
  ctx.events.push({ type: 'pickup', what: `${item.name} ${placed ? 'IN PLACE' : 'HANDED OVER'}` });
}

/** done: 1 with the item in hand, 2 once it is delivered. */
function itemSteps(ctx: Ctx): number {
  const state = ctx.world.objective.item;
  return state === 'delivered' ? 2 : state === 'carried' ? 1 : 0;
}

export function stepDeliverItem(ctx: Ctx): void {
  const o = ctx.world.objective;
  o.done = itemSteps(ctx);
  if (o.item === 'delivered') completeObjective(ctx);
}

/** Has the strike this mission waits for come in? Null = the mission has no `strikeLands` event. */
function strikeLanded(ctx: Ctx): boolean | null {
  const rules = ctx.statics.def.objective.rules;
  let any = false;
  for (const st of ctx.world.objective.timed) {
    const rule = rules[st.rule];
    if (rule?.type !== 'timedEvent' || rule.effect !== 'strikeLands') continue;
    any = true;
    if (st.fired) return true;
  }
  return any ? false : null;
}

export function stepReachDistance(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  const o = w.objective;
  const objective = statics.def.objective;
  const hasItem = objective.questItem !== undefined;
  // With nothing to place, the objective is armed from the moment he is on the ground.
  if (!hasItem && o.armedTick < 0) o.armedTick = o.groundTicks;
  if (o.complete) return;
  o.done = hasItem ? itemSteps(ctx) : 0;
  if (o.armedTick < 0) return;
  const from = objective.clearFromPropId === undefined ? undefined : propDef(statics.def, objective.clearFromPropId);
  if (!from) return;
  const clear = dist(from.x, from.z, w.ranger.x, w.ranger.z) >= (objective.clearDistance ?? 0);
  const landed = strikeLanded(ctx);
  // Judged at the moment the strike lands; with no strike to wait for, being clear is enough.
  if (landed === false) return;
  if (clear) {
    o.done = o.total;
    completeObjective(ctx);
  } else if (landed === true) failObjective(ctx, 'CAUGHT IN THE STRIKE');
}
